import React, { useEffect, useState, useMemo } from 'react';
import { Box, IconButton, Popover, Typography, styled, Grid2 } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomIcon from '../../Components/CustomIcon';
import { PrimaryFormActionButton } from '../../Components/StyledComponent';

const MAX_METRICS = 4;

const popoverPaperStyles = {
    p: '16px',
    borderRadius: '10px',
    backgroundColor: '#FFFFFF',
    boxShadow: '2px 2px 4px 0px rgba(151, 151, 151, 0.25), -2px -2px 4px 0px rgba(151, 151, 151, 0.25)'
}

const HeaderBox = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
});

const FooterBox = styled(Box)({
    padding: 0,
    display: 'flex',
    justifyContent: 'flex-end',
});


const RowActionButton = styled(IconButton)(({ active, defaultColor, isClickable, allowHover, shouldHighlight }) => ({
    width: 20,
    height: 20,
    borderRadius: '50px',
    pointerEvents: isClickable ? 'auto' : 'none',
    cursor: isClickable ? 'pointer' : 'default',
    '& .MuiSvgIcon-root': {
        color: shouldHighlight ? '#6D72F6' : defaultColor,
        fontSize: '20px',
    },
    '&:hover': {
        '& .MuiSvgIcon-root': {
            color: allowHover ? '#4D44D8' : defaultColor,
        },
    },
}));

const MetricRow = ({ metric, initialMetrics, onClick }) => {
    const { t } = useTranslation('common');

    const isSelected = useMemo(
        () => initialMetrics.some((m) => m.label === metric.label),
        [initialMetrics, metric.label]
    );
    const isFull = initialMetrics.length >= MAX_METRICS;
    const canModify = isSelected || !isFull;
    const iconName = isSelected ? 'minus-circle-icon' : 'add-circle-icon';
    const shouldHighlight = isSelected ? false : canModify;


    return (
        <Grid2 container alignItems="center" sx={{ py: 0.5, gap: 1 }}>
            <Grid2 xs={6} sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: '202px' }}>
                <RowActionButton
                    allowHover={canModify}
                    isClickable={canModify}
                    active={canModify}
                    shouldHighlight={shouldHighlight}
                    defaultColor={canModify ? '#686868' : '#A2A2A2'}
                    onClick={() => onClick(metric)}
                >
                    <CustomIcon name={iconName} />
                </RowActionButton>
                <Typography variant={canModify ? "fw400s14lh24c3D4B5A" : "fw400s14lh24cA2A2A2"}>{t(metric.label)}</Typography>
            </Grid2>
            <Grid2 xs={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <Typography variant={canModify ? "fw400s14lh24c818181" : "fw400s14lh24cA2A2A2"}>{t(metric.value)}</Typography>
            </Grid2>
        </Grid2>
    );
};

export default function DigitalTwinInfoMatrixList({
    metricList = [],
    selectedMetrics,
    applySelectedMetric,
}) {
    const { t } = useTranslation(['common', 'generic_table', 'digital_twin']);
    const [anchorEl, setAnchorEl] = useState(null);
    const [initialMetrics, setInitialMetrics] = useState(selectedMetrics);

    const open = Boolean(anchorEl);
    const id = open ? 'digital-twin-info-matrix-popover' : undefined;

    useEffect(() => {
        setInitialMetrics(selectedMetrics);
    }, [selectedMetrics]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleRowClick = (metric) => {
        let updated = [...initialMetrics];

        const isSelected = initialMetrics.some((m) => m.label === metric.label);
        if (isSelected) {
            updated = updated.filter((m) => m.label !== metric.label);
        } else {
            if (updated.length >= MAX_METRICS) {
                updated.shift();
            }
            updated.push({ ...metric, selected: true });
        }

        setInitialMetrics(updated);
    };

    const sortedMetricList = useMemo(() => {
        return [...metricList].sort((a, b) => {
            const aIndex = initialMetrics.findIndex((m) => m.label === a.label);
            const bIndex = initialMetrics.findIndex((m) => m.label === b.label);
            if (aIndex === -1 && bIndex === -1) return 0;
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
        });
    }, [metricList, initialMetrics]);

    return (
        <Box>
            <IconButton sx={{
                p: 0,
                marginBottom: '24px',
                marginLeft: '16px',
                '&:hover svg': { color: '#6D72F6' }

            }} onClick={handleClick}>
                <CustomIcon name="edit-icon" />
            </IconButton>


            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{ paper: { sx: popoverPaperStyles } }}
            >
                <HeaderBox>
                    <Typography variant="fw700s14c3D4B5A" sx={{ lineHeight: '18px' }}>{t(`digital_twin:customise_metric_view`)}</Typography>
                </HeaderBox>

                <Box sx={{ pt: 0.5, pb: 1 }}>
                    <Typography variant="fw500s10lh24c5E758D" sx={{ lineHeight: '24px', color: "#87939D" }}>{t(`digital_twin:add_metrics_subtitle`, { count: 4 })}</Typography>

                    <Grid2 container alignItems="center" sx={{ py: 0.5, gap: 1 }}>
                        <Grid2 xs={6} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: '202px' }}>
                            <Typography variant="fw500s10c949494lh14" sx={{ color: '#8D8D8D' }}>{t('digital_twin:metric')}</Typography>
                        </Grid2>
                        <Grid2 xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Typography variant="fw500s10c949494lh14" sx={{ color: '#8D8D8D' }}>{t('digital_twin:status')}</Typography>
                        </Grid2>
                    </Grid2>

                    {sortedMetricList.map((metric, index) => (
                        <MetricRow
                            key={index}
                            metric={metric}
                            initialMetrics={initialMetrics}
                            onClick={handleRowClick}
                        />
                    ))}
                </Box>

                <FooterBox>
                    <PrimaryFormActionButton
                        onClick={() => {
                            applySelectedMetric(initialMetrics);
                            handleClose();
                        }}
                    >
                        {t('actions.apply')}
                    </PrimaryFormActionButton>
                </FooterBox>
            </Popover>
        </Box>
    );
}
